/**
 * Public Signal Export V1 - Main Export Generator
 * 
 * Exports approved public signal drafts as copyable communication text
 * for manual use on different platforms.
 * 
 * BOUNDARIES:
 * - Advisory only: no execution authority
 * - No auto-posting capability
 * - Deterministic output only
 * - Fail closed on forbidden exposure patterns
 * - No mutation of inputs
 * 
 * INVARIANT:
 * Public Signal Export creates copyable communication text only.
 * It never publishes automatically.
 */

// NOTE: crypto stubbed for preview deployment (browser-safe)
const createHashStub = (input: string) => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(32, '0');
};
import {
  PublicSignalExportInputV1,
  PublicSignalExportV1,
} from './types';
import { validateExport } from './exportValidator';
import { formatForPlatform, generateCopyableText } from './platformFormatter';

/**
 * Compute deterministic export hash
 */
function computeExportHash(
  platform: string,
  finalText: string,
  sourceDraftHash: string,
  approvalHash: string
): string {
  const hashInput = JSON.stringify({
    platform,
    finalText,
    sourceDraftHash,
    approvalHash,
  }, Object.keys({platform, finalText, sourceDraftHash, approvalHash}).sort());

  return createHashStub(hashInput);
}

/**
 * Generate export timestamp (deterministic based on input)
 */
function generateExportTimestamp(sourceDraftHash: string): string {
  // Use the hash to generate a deterministic timestamp
  const hash = createHashStub(sourceDraftHash);
  const timestamp = parseInt(hash.substring(8, 16), 16);
  const date = new Date(timestamp * 1000);
  return date.toISOString();
}

/**
 * Generate public signal export
 */
export function generatePublicSignalExport(
  input: PublicSignalExportInputV1
): PublicSignalExportV1 {
  const { draft, receipt, platform } = input;

  // Validate export inputs
  const validation = validateExport(draft, receipt);
  if (!validation.valid) {
    throw new Error(`Export validation failed: ${validation.reason}`);
  }

  // Determine final text (use edited text if draft was edited)
  // Note: In a real implementation, editedDraftText would be stored in the receipt
  // For now, we'll use the draft text
  const finalText = draft.draftText;
  const title = draft.suggestedTitle || 'Public Signal';

  // Format for platform
  const formattedText = formatForPlatform(finalText, title, platform);

  // Generate copyable text
  const copyableText = generateCopyableText(formattedText, platform);

  // Compute export hash
  const exportHash = computeExportHash(
    platform,
    formattedText,
    receipt.sourceDraftHash,
    receipt.approvalHash
  );

  // Generate export timestamp
  const exportedAt = generateExportTimestamp(receipt.sourceDraftHash);

  // Build export
  const exportResult: PublicSignalExportV1 = {
    platform,
    finalText: formattedText,
    copyableText,
    exportHash,
    sourceDraftHash: receipt.sourceDraftHash,
    approvalHash: receipt.approvalHash,
    exportedAt,
  };

  return exportResult;
}
